import { verifyArrayParameters } from 'helpers/genericHelper';
import { WaterOverviewInfo } from 'providers/types/api-private';
import { apiCall } from '~/providers';
import { HealthStatusOverviewFilter, ThermalComfortStatusFilter } from '~/providers/types/overview';
import {
  GetEnergyEfficiencyRating,
  GetEnergyTrendsHistory,
  GetOverviewSummary, GetOverviewThermalComfort, GetOverviewUnitHealth,
  GetThermalComfortHistory,
  GetUnitHealthHistory,
  OverviewInfo,
} from '../types';
import { RankingType } from '../RowCardsOverview/components/EnergyEfficiencyCard/types';

type FilterOptions = {
  stateIds?: string[];
  unitIds?: number[];
  cityIds?: string[];
  healthStatus?: string;
  thermalComfortStatus?: string;
}

export async function getOverviewInfo(
  clientIds: number[],
): Promise<OverviewInfo | undefined> {
  try {
    const response = await apiCall('/clients/get-overview-info', {
      CLIENT_IDS: clientIds,
    });

    return {
      hasWater: response.HAS_WATER_DEVICES,
      hasHealth: response.HAS_HEALTH_CONTROL,
      hasEnergy: response.HAS_ENERGY_DEVICES,
      hasThermalComfort: response.HAS_THERMAL_COMFORT_CONTROL,
    };
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getWaterOverviewInfo(
  clientIds: number[],
  filterOptions?: FilterOptions,
): Promise<WaterOverviewInfo | undefined> {
  try {
    const response = await apiCall('/water/get-overview-info', {
      CLIENT_IDS: clientIds,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
      CITY_IDS: verifyArrayParameters(filterOptions?.cityIds),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getOverviewSummary(
  clientIds: number[],
  filterOptions?: FilterOptions,
): Promise<GetOverviewSummary | undefined> {
  try {
    const response = await apiCall('/clients/get-overview-summary', {
      CLIENT_IDS: clientIds,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
      CITY_IDS: verifyArrayParameters(filterOptions?.cityIds),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getOverviewUnitHealth(
  clientIds: number[],
  filterOptions?: FilterOptions,
): Promise<GetOverviewUnitHealth | undefined> {
  try {
    const response = await apiCall('/unit-health/get-overview-info', {
      CLIENT_IDS: clientIds,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
      CITY_IDS: verifyArrayParameters(filterOptions?.cityIds),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getOverviewThermalComfort(
  clientIds: number[],
  filterOptions?: FilterOptions,
): Promise<GetOverviewThermalComfort | undefined> {
  try {
    const response = await apiCall('/thermal-comfort/get-overview-info', {
      CLIENT_IDS: clientIds,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
      CITY_IDS: verifyArrayParameters(filterOptions?.cityIds),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getUnitHealthHistory(
  clientIds: number[],
  filterOptions?: FilterOptions,
): Promise<GetUnitHealthHistory | undefined> {
  try {
    const response = await apiCall('/unit-health/get-history', {
      CLIENT_IDS: clientIds,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      CITY_IDS: verifyArrayParameters(filterOptions?.cityIds),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getThermalComfortHistory(
  clientIds: number[],
  filterOptions?: FilterOptions,
): Promise<GetThermalComfortHistory | undefined> {
  try {
    const response = await apiCall('/thermal-comfort/get-history', {
      CLIENT_IDS: clientIds,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
      CITY_IDS: verifyArrayParameters(filterOptions?.cityIds),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getEnergyTrendsHistory(
  clientIds: number[],
  filterOptions?: FilterOptions,
): Promise<GetEnergyTrendsHistory | undefined> {
  try {
    const response = await apiCall('/energy/get-trends-history', {
      CLIENT_IDS: clientIds,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
      CITY_IDS: verifyArrayParameters(filterOptions?.cityIds),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getEnergyEfficiencyRating(
  clientIds: number[],
  rankingOption: RankingType,
  filterOptions?: FilterOptions,
): Promise<GetEnergyEfficiencyRating | undefined> {
  try {
    const response = await apiCall('/energy/get-efficiency-rating', {
      CLIENT_IDS: clientIds,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
      CITY_IDS: verifyArrayParameters(filterOptions?.cityIds),
      RANKING_TYPE: rankingOption,
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

function parseHealthStatus(healthStatus?: string): HealthStatusOverviewFilter[] | undefined {
  if (!healthStatus) return undefined;

  return [healthStatus as HealthStatusOverviewFilter];
}

function parseThermalComfortStatus(thermalComfortStatus?: string): ThermalComfortStatusFilter | undefined {
  if (!thermalComfortStatus) return undefined;

  return thermalComfortStatus as ThermalComfortStatusFilter;
}

export function isEqualClientsArray(clientsA?: string[], clientsB?: string[]): boolean {
  if (!clientsA && !clientsB) return true;
  return (clientsA?.length === clientsB?.length && clientsA?.every((client) => clientsB?.includes(client))) ?? false;
}
